import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from '@web-stories-wp/design-system';
import useMedia3pApi from './useMedia3pApi';
import { getMediaDetailsScope, notifyDetailFailure } from './mediaDetailsState';

// Adapt existing preview interactions without adding provider-specific UI.
export default function useProviderMediaPreview(summary, active = false) {
  const api = useMedia3pApi();
  const details = api?.actions?.details;
  const { showSnackbar } = useSnackbar();
  const [resolved, setResolved] = useState(null);
  const request = useRef(null);
  const latest = useRef(summary);
  latest.current = summary;
  const scope = getMediaDetailsScope(summary);
  const key = `${summary.mediaProviderId}:${summary.id}`;
  const needsDetails = Boolean(summary.galleryOnly);
  const load = useCallback(
    async (notify) => {
      if (!needsDetails) return latest.current;
      request.current?.abort();
      const controller = new AbortController();
      request.current = controller;
      try {
        const resource = await details.resolve(latest.current, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return null;
        setResolved({ key, scope, resource });
        return resource;
      } catch (error) {
        return notify ? notifyDetailFailure(showSnackbar, error) : null;
      }
    },
    [key, scope, needsDetails, details, showSnackbar]
  );
  // Explicit play reports failures; passive hover/focus previews fail quietly.
  const resolve = useCallback(() => load(true), [load]);
  useEffect(() => {
    if (active && needsDetails) void load(false);
    return () => request.current?.abort();
  }, [active, needsDetails, load]);
  return {
    resource:
      resolved?.key === key && resolved.scope === scope
        ? resolved.resource
        : summary,
    resolve,
  };
}
